import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { FaPlusCircle, FaMinusCircle } from 'react-icons/fa';

import DropDown from './DropDown';

const EMPTY_FILTERS = {
  nameLike: '', clientId: '', clientMode: 'ID', clientDirty: false, appliedClient: null, status: 'ANY',
  datePreset: 'ANY', customFrom: '', customTo: '', filtersCounter: 0,
};
const STATUS_OPTIONS = {
  ANY: 'Any', 0: 'Registered', 1: 'Claimed',
  2: 'Consumed', 3: 'Missed', 4: 'Pending',
};
const DATE_PRESETS = {
  ANY: 'Any time', TODAY: 'Today', LAST_7: 'Last 7 days', NEXT_7: 'Next 7 days',
  LAST_30: 'Last 30 days', NEXT_30: 'Next 30 days', THIS_MONTH: 'This month',
  PREV_MONTH: 'Previous month', CUSTOM: 'Custom range',
};
const isValidClientId = (value) => {
  const id = value.trim();
  return /^[0-9]+$/.test(id) && Number(id) >= 1 && Number(id) <= 2147483647;
};

export default class DirectPromotionsFilters extends React.Component {
  state = { ...EMPTY_FILTERS };

  componentWillUnmount() {
    clearTimeout(this.applyTimer);
  }

  changeText = (field, value) => {
    this.setState({ [field]: value, ...(field === 'clientId' ? { clientDirty: true } : {}) }, () => {
      clearTimeout(this.applyTimer);
      this.applyTimer = setTimeout(this.apply, 300);
    });
  };

  changeClientMode = (clientMode) => {
    if (!clientMode || clientMode === this.state.clientMode) return;
    clearTimeout(this.applyTimer);
    this.setState({ clientMode, clientId: '', clientDirty: false });
  };

  today = () => {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Bucharest', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date()).map(({ type, value }) => [type, value]));
    return `${parts.year}-${parts.month}-${parts.day}`;
  };

  computeDateRange = (preset) => {
    const [year, month, day] = this.today().split('-').map(Number);
    // UTC arithmetic here operates on calendar dates, not promotion timestamps.
    const ymd = (y, m, d) => new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
    const range = (from, to) => ({ customFrom: from, customTo: to });
    const today = ymd(year, month, day);
    switch (preset) {
      case 'TODAY': return range(today, today);
      case 'LAST_7': return range(ymd(year, month, day - 6), today);
      case 'NEXT_7': return range(today, ymd(year, month, day + 6));
      case 'LAST_30': return range(ymd(year, month, day - 29), today);
      case 'NEXT_30': return range(today, ymd(year, month, day + 29));
      case 'THIS_MONTH': return range(ymd(year, month, 1), ymd(year, month + 1, 0));
      case 'PREV_MONTH': return range(ymd(year, month - 1, 1), ymd(year, month, 0));
      default: return range('', '');
    }
  };

  changePreset = (datePreset) => {
    this.setState((state) => ({
      datePreset,
      ...(datePreset === 'CUSTOM'
        ? state.customFrom || state.customTo
          ? { customFrom: state.customFrom, customTo: state.customTo }
          : this.computeDateRange('TODAY')
        : this.computeDateRange(datePreset)),
    }), this.apply);
  };

  changeDate = (field, value) => {
    this.setState((state) => ({
      [field]: value,
      datePreset: value || state[field === 'customFrom' ? 'customTo' : 'customFrom']
        ? 'CUSTOM' : 'ANY',
    }), this.apply);
  };

  invalidRange = () => Boolean(
    this.state.customFrom && this.state.customTo && this.state.customFrom > this.state.customTo
  );

  invalidClientId = () => Boolean(
    this.state.clientMode === 'ID' && this.state.clientId.trim()
      && !isValidClientId(this.state.clientId)
  );

  apply = () => {
    clearTimeout(this.applyTimer);
    if (this.invalidRange() || this.invalidClientId()) return;
    const { nameLike, clientId, clientMode, clientDirty, appliedClient, status, customFrom, customTo } = this.state;
    // A mode change edits the next search; unrelated filters retain the applied client.
    const clientFilter = clientDirty ? { mode: clientMode, value: clientId.trim() } : appliedClient;
    const query = { limit: 20 };
    if (nameLike.trim()) query.nameLike = nameLike.trim();
    if (clientFilter?.value) query[clientFilter.mode === 'EMAIL' ? 'clientEmail' : 'client'] = clientFilter.value;
    if (status !== 'ANY') query.status = status;
    if (customFrom) query.validFrom = customFrom;
    if (customTo) query.validTo = customTo;
    this.setState({
      filtersCounter: Object.keys(query).length - 1,
      appliedClient: clientFilter?.value ? clientFilter : null, clientDirty: false,
    });
    this.props.applyFilters(query);
  };

  reset = () => {
    clearTimeout(this.applyTimer);
    this.setState({ ...EMPTY_FILTERS }, this.apply);
  };

  render() {
    const invalidRange = this.invalidRange();
    const invalidClientId = this.invalidClientId();
    const hasFilters = this.state.nameLike || this.state.clientId || this.state.status !== 'ANY'
      || this.state.datePreset !== 'ANY' || this.state.filtersCounter > 0;
    return (
      <Card aria-label="Direct promotion filters" sx={{ borderRadius: '16px', m: 1, backgroundColor: '#214a80' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <IconButton
            aria-label={this.props.isAdding ? 'Cancel adding' : 'Add'}
            title={this.props.isAdding ? 'Cancel adding' : 'Add'}
            onClick={this.props.toggleAdd}
            disabled={!this.props.allowEdit}
          >
            <Avatar sx={{ width: 24, height: 24, color: '#273442' }}>
              {this.props.isAdding ? <FaMinusCircle /> : <FaPlusCircle />}
            </Avatar>
          </IconButton>

          <Box role="group" aria-label="Search" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, ml: 2 }}>
            <TextField
              label="Name contains" variant="standard" value={this.state.nameLike}
              onChange={(event) => this.changeText('nameLike', event.target.value)}
              InputLabelProps={{ sx: { color: '#e3e3e5' } }}
              inputProps={{ sx: { color: '#e3e3e5' } }}
              sx={{ color: '#e3e3e5' }}
            />
            <Box role="group" aria-label="Client search" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <ToggleButtonGroup
                exclusive size="small" value={this.state.clientMode}
                onChange={(_event, mode) => this.changeClientMode(mode)}
                aria-label="Client search mode"
                sx={{
                  p: '2px', borderRadius: '18px', bgcolor: 'rgba(0, 0, 0, 0.16)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  '& .MuiToggleButtonGroup-grouped': {
                    m: 0, border: 0, px: 1.25, py: 0.25, minWidth: 40,
                    color: '#e3e3e5', textTransform: 'none', lineHeight: 1.6,
                    '&:first-of-type, &:last-of-type': { borderRadius: '14px' },
                    '&.Mui-selected, &.Mui-selected:hover': { bgcolor: '#e3e3e5', color: '#214a80' },
                    '&.Mui-focusVisible': { outline: '2px solid #fff', outlineOffset: 2 },
                  },
                }}
              >
                <ToggleButton value="ID">ID</ToggleButton>
                <ToggleButton value="EMAIL">Email</ToggleButton>
              </ToggleButtonGroup>
              <TextField
                label={this.state.clientMode === 'EMAIL' ? 'Client email' : 'Client ID'}
                type={this.state.clientMode === 'EMAIL' ? 'email' : 'text'}
                variant="standard" value={this.state.clientId} error={invalidClientId}
                onChange={(event) => this.changeText('clientId', event.target.value)}
                InputLabelProps={{ sx: { color: '#e3e3e5' } }}
                inputProps={{ sx: { color: '#e3e3e5' }, inputMode: this.state.clientMode === 'ID' ? 'numeric' : 'email' }}
                sx={{ color: '#e3e3e5', width: 200, flexShrink: 0 }}
              />
            </Box>
          </Box>

          <Box role="group" aria-label="Status">
            <DropDown
              name="Status" value={String(this.state.status)} doptions={STATUS_OPTIONS}
              onChangeFunction={(status) => this.setState({ status }, this.apply)}
              minWidth={120} size="small" marginTop={8}
            />
          </Box>

          <Box role="group" aria-label="Dates" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <DropDown
              name="Date range" value={this.state.datePreset} doptions={DATE_PRESETS}
              onChangeFunction={this.changePreset}
              minWidth={140} size="small" marginTop={8}
            />
            {this.state.datePreset !== 'ANY' && (
              <>
                <TextField
                  type="date" label="From" variant="standard"
                  value={this.state.customFrom} error={invalidRange}
                  onChange={(event) => this.changeDate('customFrom', event.target.value)}
                  InputLabelProps={{ shrink: true, sx: { color: '#e3e3e5' } }}
                  InputProps={{ sx: { color: '#e3e3e5' } }}
                  inputProps={{ title: 'Valid during this period (Europe/Bucharest)' }}
                />
                <TextField
                  type="date" label="To" variant="standard"
                  value={this.state.customTo} error={invalidRange}
                  onChange={(event) => this.changeDate('customTo', event.target.value)}
                  InputLabelProps={{ shrink: true, sx: { color: '#e3e3e5' } }}
                  InputProps={{ sx: { color: '#e3e3e5' } }}
                  inputProps={{ title: 'Valid during this period (Europe/Bucharest)' }}
                />
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', pr: 1 }}>
            <Typography variant="body2" sx={{ color: '#e3e3e5' }}>
              Items: {this.props.isLoading ? '…' : this.props.hasError ? '—' : this.props.itemsCounter || 0}
            </Typography>
            <Typography variant="body2" sx={{ color: '#e3e3e5' }}>Filters: {this.state.filtersCounter}</Typography>
            {hasFilters && (
              <IconButton aria-label="Remove Filters" title="Remove Filters" onClick={this.reset}>
                <Avatar sx={{ width: 18, height: 18, color: '#99302e' }}>
                  <HighlightOffIcon fontSize="small" />
                </Avatar>
              </IconButton>
            )}
          </Box>
        </Box>
        {!this.state.clientId && this.state.appliedClient && (
          <Typography variant="caption" sx={{ display: 'block', color: '#e3e3e5', px: 2, pb: 1 }}>
            Results still filtered by client {this.state.appliedClient.mode === 'EMAIL' ? 'email' : 'ID'}: {this.state.appliedClient.value}. Enter a new value or remove filters.
          </Typography>
        )}
        {invalidClientId && (
          <Typography role="alert" variant="caption" sx={{ display: 'block', color: '#ffcfca', px: 2, pb: 1 }}>
            Enter a numeric Client ID between 1 and 2147483647.
          </Typography>
        )}
        {invalidRange && (
          <Typography role="alert" variant="caption" sx={{ display: 'block', color: '#ffcfca', px: 2, pb: 1 }}>
            From must be on or before To.
          </Typography>
        )}
      </Card>
    );
  }
}
